import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined'
import ExclamationCircleOutlined from '@ant-design/icons/ExclamationCircleOutlined'
import LoadingOutlined from '@ant-design/icons/LoadingOutlined'
import ReloadOutlined from '@ant-design/icons/ReloadOutlined'
import WarningOutlined from '@ant-design/icons/WarningOutlined'
import { Button, Tooltip } from 'antd'
import { ReportAutoSaveState } from 'pages/reporting/shared/hooks/useReportAutoSave'
import React from 'react'
import styled from 'styled-components'

const StatusContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.forecast.secondaryText || '#6b7280'};
  padding: 0.25rem 0.75rem;
  border-radius: 0.375rem;
  background: ${({ theme }) => theme.forecast.cardGroupBackground || '#f9fafb'};
  border: 1px solid
    ${({ theme }) =>
      theme.forecast.cardDividerBackground ||
      theme.forecast.border ||
      '#e5e7eb'};
  min-width: 130px;
  justify-content: center;
`

const CompactStatusContainer = styled(StatusContainer)`
  min-width: auto;
  padding: 0.125rem 0.5rem;
  font-size: 0.75rem;
`

type StatusType = 'saving' | 'saved' | 'unsaved' | 'error'

const StatusText = styled.span<{ $status: StatusType }>`
  color: ${({ $status }) => {
    switch ($status) {
      case 'saving':
        return '#3b82f6'
      case 'saved':
        return '#10b981'
      case 'error':
        return '#ef4444'
      default:
        return '#6b7280'
    }
  }};
  font-weight: 500;
`

const StatusIcon = styled.span<{ $status: StatusType }>`
  color: ${({ $status }) => {
    switch ($status) {
      case 'saving':
        return '#3b82f6'
      case 'saved':
        return '#10b981'
      case 'error':
        return '#ef4444'
      default:
        return '#6b7280'
    }
  }};
  display: flex;
  align-items: center;
`

const RetryButton = styled(Button)`
  && {
    padding: 0;
    height: auto;
    font-size: 0.75rem;
    color: #ef4444;

    &:hover {
      color: #dc2626;
    }
  }
`

interface ReportAutoSaveIndicatorProps {
  autoSaveState: ReportAutoSaveState
  isVisible?: boolean
  compact?: boolean
  onRetry?: () => void
}

export const ReportAutoSaveIndicator: React.FC<
  ReportAutoSaveIndicatorProps
> = ({ autoSaveState, isVisible = true, compact = false, onRetry }) => {
  if (!isVisible) {
    return null
  }

  const getStatusInfo = () => {
    if (autoSaveState.error) {
      return {
        status: 'error' as const,
        text: 'Save failed',
        icon: <ExclamationCircleOutlined />,
        tooltip: `Auto-save failed: ${autoSaveState.error}. Click retry to try again.`,
      }
    }

    if (autoSaveState.isSaving) {
      return {
        status: 'saving' as const,
        text: 'Saving...',
        icon: <LoadingOutlined spin />,
        tooltip: 'Auto-saving your changes...',
      }
    }

    if (autoSaveState.lastSavedAt && !autoSaveState.hasUnsavedChanges) {
      const timeAgo = getTimeAgo(autoSaveState.lastSavedAt)
      return {
        status: 'saved' as const,
        text: `Saved ${timeAgo}`,
        icon: <CheckCircleOutlined />,
        tooltip: `Last saved at ${autoSaveState.lastSavedAt.toLocaleTimeString()}`,
      }
    }

    if (autoSaveState.hasUnsavedChanges) {
      return {
        status: 'unsaved' as const,
        text: 'Unsaved changes',
        icon: <WarningOutlined />,
        tooltip: 'You have unsaved changes. They will be auto-saved shortly.',
      }
    }

    return {
      status: 'saved' as const,
      text: 'Auto-save enabled',
      icon: <CheckCircleOutlined />,
      tooltip: 'Auto-save is enabled for this form',
    }
  }

  const { status, text, icon, tooltip } = getStatusInfo()

  const Container = compact ? CompactStatusContainer : StatusContainer

  return (
    <Tooltip title={tooltip} placement='bottom'>
      <Container>
        <StatusIcon $status={status}>{icon}</StatusIcon>
        <StatusText $status={status}>{text}</StatusText>
        {status === 'error' && onRetry && (
          <RetryButton
            type='link'
            size='small'
            icon={<ReloadOutlined />}
            onClick={(e) => {
              e.stopPropagation()
              onRetry()
            }}
          />
        )}
      </Container>
    </Tooltip>
  )
}

const getTimeAgo = (date: Date): string => {
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSeconds = Math.floor(diffMs / 1000)
  const diffMinutes = Math.floor(diffSeconds / 60)
  const diffHours = Math.floor(diffMinutes / 60)

  if (diffSeconds < 60) {
    return 'just now'
  } else if (diffMinutes < 60) {
    return `${diffMinutes}m ago`
  } else if (diffHours < 24) {
    return `${diffHours}h ago`
  } else {
    return 'today'
  }
}
